// @ts-nocheck
import { Button, Card, Code, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import {
  HoverScale,
  HoverLift,
  HoverGlow,
  PressScale,
  TiltOnHover,
  Magnetic,
} from '@/components/animations/gestures';

export default function GesturesPage() {
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Gesture Animations</Title>
        <Text c="dimmed" size="sm">
          Interactive pointer-driven micro-animations that respond to hover, press, and
          mouse movement to add tactile feedback to your UI
        </Text>
      </Stack>

      {/* HoverScale */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>HoverScale</Title>
            <Text size="sm" c="dimmed">
              Smoothly scale an element up when the pointer hovers over it
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (hover the cards):</Text>
            <SimpleGrid cols={3} spacing="md">
              <HoverScale scale={1.05}>
                <Card variant="subtle" p="md" ta="center">
                  <Text size="sm">scale 1.05</Text>
                </Card>
              </HoverScale>
              <HoverScale scale={1.1}>
                <Card variant="subtle" p="md" ta="center">
                  <Text size="sm">scale 1.1</Text>
                </Card>
              </HoverScale>
              <HoverScale scale={1.2} duration={300}>
                <Card variant="subtle" p="md" ta="center">
                  <Text size="sm">scale 1.2</Text>
                </Card>
              </HoverScale>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<HoverScale scale={1.05}>
  <Card variant="subtle" p="md">scale 1.05</Card>
</HoverScale>
<HoverScale scale={1.2} duration={300}>
  <Card variant="subtle" p="md">scale 1.2</Card>
</HoverScale>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • scale - Target scale on hover (default: 1.05)
              <br />
              • duration - Animation duration in ms (default: 200)
              <br />
              • timingFunction - CSS timing function (default: 'ease-out')
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* HoverLift */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>HoverLift</Title>
            <Text size="sm" c="dimmed">
              Raise an element with a drop shadow on hover, creating a floating effect
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (hover the cards):</Text>
            <SimpleGrid cols={3} spacing="md">
              <HoverLift lift={6} shadowIntensity="light">
                <Card variant="outline" p="md" ta="center">
                  <Text size="sm">light</Text>
                </Card>
              </HoverLift>
              <HoverLift lift={8} shadowIntensity="medium">
                <Card variant="outline" p="md" ta="center">
                  <Text size="sm">medium</Text>
                </Card>
              </HoverLift>
              <HoverLift lift={12} shadowIntensity="strong">
                <Card variant="outline" p="md" ta="center">
                  <Text size="sm">strong</Text>
                </Card>
              </HoverLift>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<HoverLift lift={8} shadowIntensity="medium">
  <Card variant="outline" p="md">medium</Card>
</HoverLift>
<HoverLift lift={12} shadowIntensity="strong">
  <Card variant="outline" p="md">strong</Card>
</HoverLift>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • lift - Vertical lift distance in pixels (default: 8)
              <br />
              • shadowIntensity - 'light' | 'medium' | 'strong' (default: 'medium')
              <br />
              • shadowColor - Shadow CSS color (default: 'rgba(0, 0, 0, 0.15)')
              <br />
              • duration - Animation duration in ms (default: 200)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* HoverGlow */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>HoverGlow</Title>
            <Text size="sm" c="dimmed">
              Surround an element with a soft colored glow when hovered
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (hover the cards):</Text>
            <SimpleGrid cols={3} spacing="md">
              <HoverGlow gradientVariant="primary">
                <Card variant="subtle" p="md" ta="center">
                  <Text size="sm">primary</Text>
                </Card>
              </HoverGlow>
              <HoverGlow gradientVariant="secondary" intensity={0.6}>
                <Card variant="subtle" p="md" ta="center">
                  <Text size="sm">secondary</Text>
                </Card>
              </HoverGlow>
              <HoverGlow color="#e64980" spread={30}>
                <Card variant="subtle" p="md" ta="center">
                  <Text size="sm">custom color</Text>
                </Card>
              </HoverGlow>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<HoverGlow gradientVariant="primary">
  <Card variant="subtle" p="md">primary</Card>
</HoverGlow>
<HoverGlow color="#e64980" spread={30}>
  <Card variant="subtle" p="md">custom color</Card>
</HoverGlow>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • color - Explicit glow color (overrides gradientVariant)
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'tertiary' (default: 'primary')
              <br />
              • intensity - Glow opacity, 0 to 1 (default: 0.4)
              <br />
              • spread - Glow spread in pixels (default: 20)
              <br />
              • duration - Animation duration in ms (default: 200)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* PressScale */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>PressScale</Title>
            <Text size="sm" c="dimmed">
              Briefly shrink an element while it is pressed for a tactile click response
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (press and hold):</Text>
            <Group>
              <PressScale scale={0.95} onClick={() => {}}>
                <Card variant="gradient" gradientVariant="primary" p="md" ta="center">
                  <Text c="white" fw={600}>Press me</Text>
                </Card>
              </PressScale>
              <PressScale scale={0.9} onClick={() => {}}>
                <Button>Pressable button</Button>
              </PressScale>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<PressScale scale={0.95} onClick={() => {}}>
  <Card variant="gradient" gradientVariant="primary" p="md">
    Press me
  </Card>
</PressScale>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • scale - Scale while pressed (default: 0.95)
              <br />
              • duration - Animation duration in ms (default: 100)
              <br />
              • onClick - Optional click handler (also sets a pointer cursor)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* TiltOnHover */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>TiltOnHover</Title>
            <Text size="sm" c="dimmed">
              Apply a 3D perspective tilt that follows the pointer across the element
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (move the pointer over the cards):</Text>
            <SimpleGrid cols={2} spacing="md">
              <TiltOnHover maxTilt={15}>
                <Card variant="gradient" gradientVariant="primary" p="xl" ta="center">
                  <Text c="white" fw={600}>Tilt 15°</Text>
                </Card>
              </TiltOnHover>
              <TiltOnHover maxTilt={25} scale={1.05}>
                <Card variant="gradient" gradientVariant="secondary" p="xl" ta="center">
                  <Text c="white" fw={600}>Tilt 25° + scale</Text>
                </Card>
              </TiltOnHover>
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<TiltOnHover maxTilt={15}>
  <Card variant="gradient" gradientVariant="primary" p="xl">
    Tilt 15°
  </Card>
</TiltOnHover>
<TiltOnHover maxTilt={25} scale={1.05}>
  <Card>Tilt 25° + scale</Card>
</TiltOnHover>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • maxTilt - Maximum tilt angle in degrees (default: 15)
              <br />
              • perspective - 3D perspective depth in pixels (default: 1000)
              <br />
              • scale - Scale applied while tilting (default: 1.02)
              <br />
              • reset - Reset to flat on mouse leave (default: true)
              <br />
              • duration - Reset animation duration in ms (default: 200)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Magnetic */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Magnetic</Title>
            <Text size="sm" c="dimmed">
              Pull an element toward the pointer when it enters a configurable radius
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (hover near the buttons):</Text>
            <Group gap="xl">
              <Magnetic strength={0.3}>
                <Button>Magnetic</Button>
              </Magnetic>
              <Magnetic strength={0.6} radius={140}>
                <Button variant="outline">Stronger pull</Button>
              </Magnetic>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<Magnetic strength={0.3}>
  <Button>Magnetic</Button>
</Magnetic>
<Magnetic strength={0.6} radius={140}>
  <Button variant="outline">Stronger pull</Button>
</Magnetic>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • strength - Pull strength, 0 to 1 (default: 0.3)
              <br />
              • radius - Activation radius in pixels (default: 100)
              <br />
              • duration - Snap-back animation duration in ms (default: 150)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Interactive Cards & Tiles</Text>
              <Text size="xs" c="dimmed">
                Wrap dashboard cards in HoverLift or HoverScale to signal they are clickable.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Buttons & Calls to Action</Text>
              <Text size="xs" c="dimmed">
                Combine PressScale with HoverGlow for buttons that feel responsive on click.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Hero & Feature Sections</Text>
              <Text size="xs" c="dimmed">
                Use TiltOnHover on hero artwork or feature cards for a premium 3D feel.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Primary Actions</Text>
              <Text size="xs" c="dimmed">
                Apply Magnetic to a single key action so the pointer naturally gravitates to it.
              </Text>
            </Stack>
          </SimpleGrid>

          <Text size="xs" c="dimmed">
            All gesture animations respect the user's reduced-motion preference and disable
            movement automatically when it is enabled.
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}
